import { isPlatform } from '@ionic/react';
import { AuthService } from 'ionic-appauth';
import {
  CapacitorBrowser,
  CapacitorSecureStorage,
} from 'ionic-appauth/lib/capacitor';
import { User } from '../model/models';

import { AxiosRequestor } from './AxiosService';
import { useLogger } from './LogService';

export const useAuth = () => {
  const console = useLogger('AuthService');
  return Auth.getInstance(console);
};

export class Auth {
  public static LOGIN_PATH = '/loginredirect';
  public static LOGOUT_PATH = '/endredirect';

  private static authService: GiaAuthService | undefined;

  public static storage: CapacitorSecureStorage = new CapacitorSecureStorage();

  private static wpToken: string = '';

  public static logger = console;

  public static signIn(): Promise<void> {
    // first ensure to be disconnected from gia to avoid 'loading...' issue.
    // Auth.signOut();
    return Auth.getInstance(Auth.logger).signIn();
  }

  public static signOut(clearOfflineData?: boolean): Promise<void> {
    /* Auth.logger.debug("clearing token_response...");
    await Auth.storage
      .removeItem("token_response")
      .then(() => {
        Auth.logger.info("token_response cleared");
      })
      .catch((e) => {
        Auth.logger.warn("token_response not cleared", e);
      }); */
    Auth.logger.debug('clearing wp token...');
    return Auth.clearWpToken()
      .finally(() => {
        Auth.logger.debug(`signOut(${clearOfflineData})...`);
        Auth.getInstance(Auth.logger).signOut();
        Auth.logger.info('signOut() done');
        Auth.logger.info('Removing Offline datas');
        if (clearOfflineData === true) {
          Auth.clear('offline-user-datas');
          localStorage.removeItem('global-state');
        }
      })
      .catch((e) => {
        Auth.logger.warn('clear wp token error', e);
      })
      .then(() => {
        Auth.logger.info('wp token cleared');
      });
  }

  public static async getWpToken(): Promise<string> {
    if (!Auth.wpToken) {
      const stored = await Auth.load('x-wp-token');
      Auth.wpToken = stored ? stored : '';
    }
    if (Auth.wpToken) {
      Auth.logger.debug('Using wpToken: ' + Auth.wpToken);
    }
    return Auth.wpToken;
  }

  public static async setWpToken(token: string): Promise<void> {
    Auth.logger.debug('Set wpToken: ' + token);
    if (token !== Auth.wpToken) {
      // Update only if has changed
      await Auth.save('x-wp-token', token);
      Auth.wpToken = token;
      if (token) {
        Auth.logger.debug('Set wpToken updated!');
      }
    }
  }

  public static async clearWpToken(): Promise<void> {
    await Auth.clear('x-wp-token').finally(() => {
      Auth.wpToken = '';
      Auth.logger.info('Clear wpToken');
    });
  }

  public static getStorage(): CapacitorSecureStorage {
    return this.storage;
  }

  public static async save(key: string, value: string): Promise<void> {
    return Auth.getStorage().setItem(key, value);
  }

  public static async load(key: string): Promise<string | null> {
    return Auth.getStorage().getItem(key);
  }

  private static async clear(key: string): Promise<void> {
    return Auth.getStorage().removeItem(key);
  }

  public static isApp(): boolean {
    return isPlatform('capacitor');
  }

  public static isFromApp(): boolean {
    return window?.location?.href?.startsWith(Auth.getAppBaseUrl());
  }

  public static getAppBaseUrl(): string {
    return `${process.env.REACT_APP_DEEP_LINK_SCHEME_ALT}://${process.env.REACT_APP_DEEP_LINK_HOST_ALT}`;
  }

  public static getWebBaseUrl(): string {
    return `${process.env.REACT_APP_DEEP_LINK_SCHEME}://${process.env.REACT_APP_DEEP_LINK_HOST}`;
  }

  public static getAppLoginUrl(): string {
    return Auth.getAppBaseUrl() + Auth.LOGIN_PATH;
  }

  public static getAppLogoutUrl(): string {
    return Auth.getAppBaseUrl() + Auth.LOGOUT_PATH;
  }

  private static buildAuthInstance(storage: CapacitorSecureStorage) {
    const authService = new GiaAuthService(
      new CapacitorBrowser(),
      storage,
      new AxiosRequestor()
    );
    const isCap = isPlatform('capacitor');
    const baseUrl = isCap ? Auth.getAppBaseUrl() : window.location.origin;

    authService.authConfig = {
      client_id: process.env.REACT_APP_AUTH_CLIENT_ID
        ? process.env.REACT_APP_AUTH_CLIENT_ID
        : '',
      server_host: process.env.REACT_APP_AUTH_URL
        ? process.env.REACT_APP_AUTH_URL
        : '',
      redirect_url: baseUrl + Auth.LOGIN_PATH,
      end_session_redirect_url: baseUrl + Auth.LOGOUT_PATH,
      scopes: 'openid profile email address giaprofile',
      pkce: true,
    };

    authService.init();
    return authService;
  }

  public static getInstance(logger): GiaAuthService {
    if (!this.authService) {
      this.logger = logger;
      this.authService = this.buildAuthInstance(this.storage);
    }

    return this.authService;
  }

  public static canUseOfflineDatas() {
    return Auth.isApp();
  }

  public static showQRCodeOffline(user): boolean {
    return Auth.canUseOfflineDatas() && user?.qrcode;
  }

  public static showEmergencyContactOffline(user): boolean {
    return (
      Auth.canUseOfflineDatas() &&
      user?.emergency_contacts &&
      Array.isArray(user.emergency_contacts) &&
      user.emergency_contacts.length > 0
    );
  }

  public static saveOfflineDatas(user: User) {
    if (Auth.canUseOfflineDatas()) {
      Auth.logger.info('Saving data for offline...');
      this.save('offline-user-datas', JSON.stringify(user));
    }
  }

  public static loadOfflineDatas(): Promise<User | undefined> {
    if( Auth.isApp() ) {
      return Auth.load('offline-user-datas')
      .then(
        (data) => {
          Auth.logger.info('loadOfflineDatas() loaded !!!');
          if (typeof data === 'string') {
            const u = JSON.parse(data) as User;
            u.offline = true;
            return u;
          } else {
            Auth.logger.info('loadOfflineDatas() no valid data');
            throw new Error('no-valid-offline-user-datas');
          }
        },
        (data) => {
          Auth.logger.warn('loadOfflineDatas() something rejected');
          return undefined;
        }
      )
      .catch((err) => {
        Auth.logger.error('error loading offline-user-datas', err);
        throw err;
      });
    } else {
      return Promise.resolve(undefined);
    }
    
  }
}
class GiaAuthService extends AuthService {
  public getBrowser() {
    return this.browser;
  }
}
